import React, { useState } from 'react';
import { useApiClient } from '../../api/ApiClientContext';
import { useQueryClient } from '@tanstack/react-query';
import styles from './MoodSelectionPage.module.scss';
import { useConflictSafeMutation } from '../../hooks/useConflictSafeMutation';
import { Icon, IconName } from '../../assets/icons/Icon';

interface MoodOption {
  value: number;
  icon: IconName;
  label: string;
  description: string;
}

const MOOD_OPTIONS: MoodOption[] = [
  { value: 1, icon: 'MoodAwfulIcon', label: 'Awful', description: "Awful.\nI am not satisfied with my workplace at all." },
  { value: 2, icon: 'MoodNotGoodIcon', label: 'Not good', description: "Not good.\n(tired, sick or annoyed)" },
  { value: 3, icon: 'MoodFineIcon', label: 'Fine', description: "Fine.\nFeeling as it is midweek." },
  { value: 4, icon: 'MoodGoodIcon', label: 'Good', description: "Good.\nFeeling productive." },
  { value: 5, icon: 'MoodGreatIcon', label: 'Great', description: "Great!\nFeeling inspired." },
];

interface MoodSelectionPageProps {
  userName?: string;
  onMoodSubmitted?: () => void;
  onClose?: () => void;
}

const MoodSelectionPage: React.FC<MoodSelectionPageProps> = ({
  userName = "there",
  onMoodSubmitted,
  onClose,
}) => {
  const [selectedMood, setSelectedMood] = useState<number | null>(null);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const logMoodMutation = useConflictSafeMutation({
    mutationFn: async (moodValue: number) => {
      return await apiClient.api.logMoodApiV1MoodPost({
        mood_value: moodValue
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFlowStatus'] });
      onMoodSubmitted?.();
    },
    onError: (error) => {
      console.error('Failed to log mood:', error);
    }
  });

  const handleSubmit = () => {
    if (selectedMood !== null) {
      logMoodMutation.mutate(selectedMood);
    }
  };

  const handleSkip = () => {
    onClose?.();
  };

  const isLoading = logMoodMutation.isPending;

  const getIconClassName = (value: number) => {
    const isDimmed = selectedMood !== null && selectedMood !== value;
    return `${styles.moodIcon}${isDimmed ? ` ${styles.moodIconDimmed}` : ''}`;
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>Hi, {userName}!</h1>
            <div className={styles.subtitleContainer}>
              <p className={styles.subtitle}>How are you feeling today?</p>
            </div>
          </div>
        </div>

        <div className={styles.moodOptions}>
          {MOOD_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={`${styles.moodOptionWrapper} ${
                selectedMood === option.value ? styles.selected : ''
              }`}
            >
              <button
                className={styles.moodOption}
                onClick={() => setSelectedMood(option.value)}
                disabled={isLoading}
              >
                <Icon
                  iconName={option.icon}
                  width="52"
                  height="52"
                  className={getIconClassName(option.value)}
                />
              </button>
              <div className={styles.moodDescription}>
                {option.description}
              </div>
            </div>
          ))}
        </div>

        {logMoodMutation.isError && (
          <div className={styles.error}>
            Failed to submit mood. Please try again.
          </div>
        )}
      </div>

      <div className={styles.welcomeFooter}>
        <div className={styles.submitSection}>
          {selectedMood !== null ? (
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className={styles.submitButton}
            >
              {logMoodMutation.isPending ? 'Submitting...' : 'Continue'}
            </button>
          ) : (
            <button
              onClick={handleSkip}
              disabled={isLoading}
              className={styles.skipButton}
            >
              Skip
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoodSelectionPage;
